<template lang="pug">
.payment-config
  .title Configuración de pagos
  .payment-content
    .hint(v-if="loading") Cargando configuración…
    shActionButton(v-if="!loading && !config" text="Reintentar" :action="load")
    .config-card.history-status(role="status" aria-live="polite" :aria-busy="historyLoading")
      .history-heading
        .dark Proyección del mes
        button.add(type="button" @click="loadHistory" :disabled="historyLoading") {{ historyLoading ? 'Consultando…' : 'Actualizar datos' }}
      .history-loading(v-if="historyLoading")
        span.loading-spinner(aria-hidden="true")
        span Cargando pagos aprobados del mes anterior y del actual…
      .hint.error(v-else-if="historyError") {{ historyError }}
      .hint(v-else-if="history") Base: {{ monthLabel(history.previousMonth) }} · Avance: {{ monthLabel(history.currentMonth) }} · Actualizado {{ updatedLabel }}
    template(v-if="config")
      .config-card
        .card-heading
          .dark Propietarios de cuentas Wompi
          button.add(type="button" @click="addOwner" :disabled="saving") + Agregar propietario
        .owner(v-for="owner in config.owners" :key="owner.id")
          .owner-heading
            label.check
              input(type="checkbox" v-model="owner.enabled" :disabled="saving")
              span Habilitado
            input.owner-name(type="text" v-model="owner.name" placeholder="Nombre del propietario" :disabled="saving" maxlength="100" :aria-label="'Nombre del propietario'")
            button.add.credentials-disclosure(type="button" @click="toggleCredentials(owner)" :aria-expanded="owner.credentialsExpanded") {{ owner.credentialsExpanded ? '−' : '+' }} Credenciales
            button.remove(type="button" @click="removeOwner(owner)" :disabled="saving") Eliminar
          .credentials(v-if="owner.credentialsExpanded")
            .credentials-title Credenciales Wompi
            .credential-grid
              .credential-field(v-for="field in credentialFields" :key="field.key")
                label(:for="owner.id + '-' + field.key") {{ field.label }}
                .credential-input
                  input(:id="owner.id + '-' + field.key" :type="owner.credentialVisibility[field.key] ? 'text' : 'password'" v-model="owner.credentials[field.key]" autocomplete="new-password" spellcheck="false" autocapitalize="off" :placeholder="field.label" :disabled="saving")
                  button.credential-toggle(type="button" @click="toggleCredential(owner, field.key)" :disabled="saving" :aria-label="owner.name + ': ' + (owner.credentialVisibility[field.key] ? 'Ocultar ' : 'Mostrar ') + field.label" :title="(owner.credentialVisibility[field.key] ? 'Ocultar ' : 'Mostrar ') + field.label" :aria-pressed="owner.credentialVisibility[field.key]")
                    svg(viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true")
                      path(d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z")
                      circle(cx="12" cy="12" r="3")
                      path(v-if="owner.credentialVisibility[field.key]" d="m3 3 18 18")

      .config-card(v-for="brand in config.brands" :key="brand.brand")
        .brand-heading
          .dark.brand-name {{ brand.brand }}
        .budget-grid
          label.budget-field
            span Mes normal · COP
            input(type="number" v-model.number="budgets[brand.brand].normal" min="0" step="1000000" :aria-label="brand.brand + ': presupuesto mes normal'")
          label.budget-field
            span Mes de promoción · COP
            input(type="number" v-model.number="budgets[brand.brand].promotion" min="0" step="1000000" :aria-label="brand.brand + ': presupuesto mes de promoción'")
          label.check.sale-toggle
            input(type="checkbox" v-model="budgets[brand.brand].sale" :aria-label="brand.brand + ': Sale'")
            span Sale
        .hint.error(v-if="!projections[brand.brand].validBudget") Escribe un presupuesto válido mayor o igual a cero.
        .dark Distribución de nuevos pagos
        .table-scroll
          table.distribution
            thead
              tr
                th Propietario
                th(v-for="method in brand.methods" :key="method.method") {{ methodLabels[method.method] }}
            tbody
              tr(v-for="owner in config.owners" :key="owner.id" :class="{ inactive: !owner.enabled }")
                th {{ owner.name || 'Nuevo propietario' }}
                td(v-for="method in brand.methods" :key="method.method")
                  .allocation
                    input(type="checkbox" v-model="allocation(method, owner.id).enabled" :disabled="saving || !owner.enabled || isRestricted(owner, method)" :aria-label="owner.name + ': habilitar ' + methodLabels[method.method]")
                    input.percentage(type="number" v-model.number="allocation(method, owner.id).percentage" min="0" max="100" step="0.01" :disabled="saving || !owner.enabled || isRestricted(owner, method) || !allocation(method, owner.id).enabled" :aria-label="owner.name + ': porcentaje de ' + methodLabels[method.method]")
                    span %
              tr.totals
                th Total habilitado
                td(v-for="method in brand.methods" :key="method.method" :class="{ invalid: !validTotal(method) }") {{ total(method) }}%
        .hint.error(v-if="brand.methods.some(method => !validTotal(method))") Ajusta los porcentajes para que cada método sume 100% entre propietarios habilitados.

        .projection-section(v-if="history")
          .dark Estimación por propietario
          .hint.error(v-if="!projections[brand.brand].previousTotal") No hay pagos aprobados de los métodos configurados en el mes anterior. Se muestra el avance, pero no es posible estimar el reparto.
          .table-scroll
            table.distribution.projection-table
              thead
                tr
                  th Propietario
                  th Recibido este mes
                  th Proyección
                  th Estimado al finalizar el mes
              tbody
                tr(v-for="row in projections[brand.brand].rows" :key="row.id")
                  th
                    div {{ row.name }}
                    .method-share(v-if="row.historicalOnly") Sin asignación actual
                  td.projection-value {{ money(row.total.current) }}
                  td.projection-value {{ money(row.total.expected) }}
                  td.owner-projection-total.projection-value {{ money(row.total.closing) }}
                tr.totals
                  th Total
                  td {{ money(projections[brand.brand].currentTotal) }}
                  td {{ money(projections[brand.brand].closing === null ? null : projections[brand.brand].closing - projections[brand.brand].currentTotal) }}
                  td {{ money(projections[brand.brand].closing) }}

      .save-row
        shActionButton(text="Guardar" textRunning="Guardando" :action="save" :principalColor="true" :disabled="saving || !canSave")
</template>

<script>
import gql from '@my/graphql'
import queries from '@/graphql/paymentConfig'
import modals from '@my/store/modals'
import shActionButton from '@/components/elements/ActionButton'
import { projectionDefaults, restrictPaymentMethod, buildProjection } from './paymentProjection'

const emptyCredentials = () => ({ publicKey: '', privateKey: '', integrity: '', events: '' })
const hiddenCredentials = () => ({ publicKey: false, privateKey: false, integrity: false, events: false })

export default {
  name: 'PaymentConfig',
  components: { shActionButton },
  data () {
    return {
      config: null,
      loading: false,
      saving: false,
      nextOwner: 0,
      history: null,
      historyLoading: false,
      historyError: '',
      budgets: projectionDefaults(),
      methodLabels: { pse: 'PSE', visa: 'Visa', mastercard: 'Mastercard', bancolombia: 'Bancolombia', nequi: 'Nequi' },
      credentialFields: [
        { key: 'publicKey', label: 'Llave pública' },
        { key: 'privateKey', label: 'Llave privada' },
        { key: 'integrity', label: 'Secreto de integridad' },
        { key: 'events', label: 'Secreto de eventos' }
      ]
    }
  },
  computed: {
    projections () {
      const results = {}
      if (this.config) {
        this.config.brands.forEach(brand => {
          results[brand.brand] = buildProjection(brand, this.config.owners, this.history, this.budgets[brand.brand])
        })
      }
      return results
    },
    updatedLabel () {
      if (!this.history) return ''
      return new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', dateStyle: 'short', timeStyle: 'short' }).format(new Date(this.history.updatedAt))
    },
    canSave () {
      if (!this.config || !this.config.owners.length) return false
      return this.config.owners.every(owner => owner.name.trim() && (owner.credentialsConfigured || Object.keys(owner.credentials).every(field => owner.credentials[field].trim()))) &&
        this.config.brands.every(brand => brand.methods.every(method => this.validTotal(method) && method.allocations.every(a => typeof a.percentage === 'number' && isFinite(a.percentage) && a.percentage >= 0 && a.percentage <= 100)))
    }
  },
  mounted () {
    this.load()
    this.loadHistory()
  },
  methods: {
    money (value) {
      if (value === null || !Number.isFinite(value)) return '—'
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value)
    },
    monthLabel (month) {
      return new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', month: 'long', year: 'numeric' }).format(new Date(month + '-15T12:00:00-05:00'))
    },
    isRestricted (owner, method) {
      return restrictPaymentMethod(owner, method.method)
    },
    async loadHistory () {
      if (this.historyLoading) return
      this.historyLoading = true
      this.historyError = ''
      try {
        const response = await gql.query(queries.HISTORY)
        if (!response || !response.data || !response.data.getPaymentHistory) throw new Error('No report')
        this.history = response.data.getPaymentHistory
      } catch (error) {
        this.historyError = 'No se pudieron cargar los pagos aprobados. Usa Actualizar datos para reintentar.'
      } finally {
        this.historyLoading = false
      }
    },
    toggleCredentials (owner) {
      owner.credentialsExpanded = !owner.credentialsExpanded
      if (!owner.credentialsExpanded) owner.credentialVisibility = hiddenCredentials()
    },
    setConfig (data) {
      const config = JSON.parse(JSON.stringify(data))
      config.owners.forEach(owner => {
        owner.credentials = Object.assign(emptyCredentials(), owner.credentials || {})
        owner.credentialVisibility = hiddenCredentials()
        owner.credentialsExpanded = false
      })
      config.brands.forEach(brand => brand.methods.forEach(method => {
        config.owners.forEach(owner => {
          if (!method.allocations.some(a => a.ownerId === owner.id)) method.allocations.push({ ownerId: owner.id, enabled: false, percentage: 0 })
        })
      }))
      this.config = config
    },
    async load () {
      this.loading = true
      try {
        const response = await gql.query(queries.GET)
        if (response && response.data && response.data.getPaymentConfig) this.setConfig(response.data.getPaymentConfig)
      } catch (error) {
        modals.showMessage({ message: 'No se pudo cargar la configuración de pagos', type: 'error' })
      } finally {
        this.loading = false
      }
    },
    toggleCredential (owner, field) {
      this.$set(owner.credentialVisibility, field, !owner.credentialVisibility[field])
    },
    allocation (method, ownerId) {
      return method.allocations.find(a => a.ownerId === ownerId)
    },
    total (method) {
      return Math.round(method.allocations.reduce((sum, a) => {
        const owner = this.config.owners.find(o => o.id === a.ownerId)
        return sum + (owner && owner.enabled && a.enabled ? Number(a.percentage) || 0 : 0)
      }, 0) * 1000000) / 1000000
    },
    validTotal (method) {
      return Math.abs(this.total(method) - 100) < 0.000001
    },
    addOwner () {
      const id = 'new-' + (++this.nextOwner)
      this.config.owners.push({ id, name: '', enabled: true, credentialsConfigured: false, credentials: emptyCredentials(), credentialVisibility: hiddenCredentials(), credentialsExpanded: false })
      this.config.brands.forEach(brand => brand.methods.forEach(method => {
        method.allocations.push({ ownerId: id, enabled: false, percentage: 0 })
      }))
    },
    removeOwner (owner) {
      this.config.owners = this.config.owners.filter(o => o.id !== owner.id)
      this.config.brands.forEach(brand => brand.methods.forEach(method => {
        method.allocations = method.allocations.filter(a => a.ownerId !== owner.id)
      }))
    },
    async save () {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const config = {
          revision: this.config.revision,
          owners: this.config.owners.map(owner => ({ id: owner.id, name: owner.name.trim(), enabled: owner.enabled, credentials: owner.credentials })),
          brands: this.config.brands
        }
        const response = await gql.mutate(queries.UPDATE, { config })
        if (response && response.data && response.data.updatePaymentConfig) {
          this.setConfig(response.data.updatePaymentConfig)
          modals.showMessage({ message: 'Configuración de pagos guardada', type: 'ok' })
        }
      } catch (error) {
        modals.showMessage({ message: 'No se pudo guardar la configuración de pagos', type: 'error' })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.payment-content
  max-width 1100px
  margin 0 auto
  padding 1rem
  display flex
  flex-direction column
  gap 1.5rem
.config-card
  border 1px solid rgba(0, 0, 0, .1)
  border-radius 1rem
  padding 1.5rem
  display flex
  flex-direction column
  gap 1rem
.card-heading, .owner-heading, .allocation, .save-row, .check, .history-heading, .brand-heading
  display flex
  align-items center
  gap .6rem
.card-heading, .owner-heading, .save-row
  flex-wrap wrap
.card-heading, .history-heading, .brand-heading
  justify-content space-between
.history-heading, .brand-heading
  flex-wrap wrap
.hint
  font-size .85rem
  opacity .75
  line-height 1.5
.owner
  padding 1rem 0
  border-top 1px solid rgba(0, 0, 0, .1)
.owner-name
  flex 1
  min-width 160px
input[type='text'], input[type='password'], input[type='number']
  padding .45rem .6rem
  border 1px solid rgba(0, 0, 0, .2)
  border-radius .3rem
  box-sizing border-box
  background transparent
  color inherit
input[type='checkbox']
  accent-color var(--color-principal)
  width 16px
  height 16px
  cursor pointer
.credentials
  margin-top .8rem
  .credentials-title
    font-weight 600
    font-size .9rem
  .hint
    margin-top .6rem
.credential-grid
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 1rem
  margin-top 1rem
  .credential-field
    display flex
    flex-direction column
    gap .4rem
    min-width 0
    font-size .85rem
.credential-input
  position relative
  input
    width 100%
    padding-right 2.8rem
.credential-toggle
  position absolute
  right .3rem
  top 50%
  transform translateY(-50%)
  display flex
  align-items center
  justify-content center
  width 2rem
  height 2rem
  border none
  border-radius .3rem
  background transparent
  color inherit
  cursor pointer
  &:hover
    background rgba(0, 0, 0, .05)
  &:focus-visible
    outline 2px solid var(--color-principal)
    outline-offset 1px
  svg
    width 20px
    height 20px
    pointer-events none
.table-scroll
  overflow-x auto
.distribution
  width 100%
  border-collapse collapse
  th, td
    padding .8rem .6rem
    text-align left
    border-bottom 1px solid rgba(0, 0, 0, .08)
    white-space nowrap
  th
    font-size .85rem
  .inactive
    opacity .5
  .totals
    font-weight bold
.percentage
  width 75px
.brand-name
  text-transform capitalize
.invalid, .error
  color #c00
button.add, button.remove
  padding .4rem .8rem
  border none
  border-radius .3rem
  cursor pointer
  font-size .85rem
button.add
  background rgba(0, 0, 0, .05)
button.remove
  color #c00
  background transparent
button:disabled, input:disabled
  cursor default
  opacity .6
.budget-grid
  display grid
  gap 1rem
.budget-grid
  grid-template-columns repeat(2, minmax(0, 1fr)) auto
  max-width 700px
  align-items end
.sale-toggle
  min-height 34px
.budget-field
  display flex
  flex-direction column
  gap .4rem
  font-size .85rem
  input
    width 100%
.projection-section
  border-top 1px solid rgba(0, 0, 0, .1)
  padding-top 1rem
  display flex
  flex-direction column
  gap .8rem
.projection-table
  min-width 100%
  th, td
    padding .8rem .35rem
  td
    min-width 125px
    vertical-align top
.method-share
  font-size .7rem
  font-weight normal
  opacity .65
  margin-top .3rem
.projection-value
  font-size .85rem
  font-variant-numeric tabular-nums
.owner-projection-total
  background rgba(0, 0, 0, .025)
.history-loading
  display flex
  align-items center
  gap .7rem
  font-size .85rem
.loading-spinner
  width 15px
  height 15px
  border 2px solid rgba(0, 0, 0, .15)
  border-top-color var(--color-principal, #333)
  border-radius 50%
  animation loading-rotation .8s linear infinite
@keyframes loading-rotation
  to
    transform rotate(360deg)
@media (prefers-reduced-motion: reduce)
  .loading-spinner
    animation none
@media (max-width: 600px)
  .config-card
    padding 1rem
  .credential-grid, .budget-grid
    grid-template-columns 1fr
</style>



// WEBPACK FOOTER //
// src/components/pages/paymentConfig/PaymentConfig.vue